import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { recipes as recipesApi, feedback as feedbackApi, auth as authApi } from '../lib/api';

function Settings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, updateSettings, deleteAccount, theme, setTheme, measurementSystem, setMeasurementSystem } = useAuthStore();

  const [name, setName] = useState(user?.name || '');
  const [localTheme, setLocalTheme] = useState(theme);
  const [localMeasurement, setLocalMeasurement] = useState(measurementSystem);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState('Feature Request');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackError, setFeedbackError] = useState('');
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Check if user can change password (email auth only)
  const canChangePassword = user?.authProvider === 'email' || !user?.authProvider;

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      await updateSettings({
        name,
        theme: localTheme,
        measurementSystem: localMeasurement,
      });
      setTheme(localTheme);
      setMeasurementSystem(localMeasurement);
      setSuccess('Settings saved successfully');
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setPasswordError('All password fields are required');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setIsChangingPassword(true);

    try {
      await authApi.changePassword(currentPassword, newPassword);
      setPasswordSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const exportMutation = useMutation({
    mutationFn: () => recipesApi.export(),
    onSuccess: (data) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recipe-grabber-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
  });

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (!data.recipes || !Array.isArray(data.recipes)) {
          throw new Error('Invalid file format');
        }

        await recipesApi.import(data.recipes);
        queryClient.invalidateQueries({ queryKey: ['recipes'] });
        setSuccess(`Imported ${data.recipes.length} recipes`);
      } catch (err) {
        setError(err.message || 'Failed to import recipes');
      }
    };
    reader.readAsText(file);
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
      navigate('/login');
    } catch (err) {
      setError(err.message || 'Failed to delete account');
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto animate-fadeIn">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Settings</h1>

      {/* Success message */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg flex items-center justify-between">
          {success}
          <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-700">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg flex items-center justify-between">
          {error}
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-700">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* Profile */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Profile</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email
            </label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="input bg-gray-50 dark:bg-gray-900 cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
            />
          </div>
        </div>
      </div>

      {/* Change Password - Only for email auth users */}
      {canChangePassword && (
        <div className="card p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Change Password</h2>

          {passwordSuccess && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg text-sm flex items-center justify-between">
              {passwordSuccess}
              <button onClick={() => setPasswordSuccess('')} className="text-green-600 hover:text-green-700">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}

          {passwordError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg text-sm flex items-center justify-between">
              {passwordError}
              <button onClick={() => setPasswordError('')} className="text-red-600 hover:text-red-700">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="input"
                placeholder="Enter current password"
                autoComplete="current-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input"
                placeholder="Enter new password"
                autoComplete="new-password"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Must be at least 8 characters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                className="input"
                placeholder="Confirm new password"
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={handleChangePassword}
              disabled={isChangingPassword}
              className="btn btn-secondary"
            >
              {isChangingPassword ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </div>
      )}

      {/* Preferences */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Preferences</h2>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Theme
            </label>
            <div className="flex gap-3">
              {[
                { value: 'light', label: 'Light' },
                { value: 'dark', label: 'Dark' },
                { value: 'system', label: 'System' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setLocalTheme(option.value)}
                  className={`btn flex-1 ${
                    localTheme === option.value ? 'btn-primary' : 'btn-secondary'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Measurement System
            </label>
            <div className="flex gap-3">
              {[
                { value: 'imperial', label: 'Imperial (cups, oz)' },
                { value: 'metric', label: 'Metric (g, ml)' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setLocalMeasurement(option.value)}
                  className={`btn flex-1 ${
                    localMeasurement === option.value ? 'btn-primary' : 'btn-secondary'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end mb-8">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="btn btn-primary"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Data Management */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Data Management</h2>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">Export Recipes</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Download all your recipes as a JSON file
              </p>
            </div>
            <button
              onClick={() => exportMutation.mutate()}
              disabled={exportMutation.isPending}
              className="btn btn-secondary"
            >
              {exportMutation.isPending ? 'Exporting...' : 'Export'}
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">Import Recipes</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Import recipes from a JSON file
              </p>
            </div>
            <label className="btn btn-secondary cursor-pointer">
              Import
              <input
                type="file"
                accept=".json"
                onChange={handleImport}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Support */}
      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Support</h2>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white">Send Feedback</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Report issues, suggest features, or ask questions
            </p>
          </div>
          <button
            onClick={() => setShowFeedbackModal(true)}
            className="btn btn-secondary"
          >
            Send Feedback
          </button>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="card p-6 border-red-200 dark:border-red-900">
        <h2 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">Danger Zone</h2>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white">Delete Account</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Permanently delete your account and all recipes
            </p>
          </div>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="btn bg-red-500 text-white hover:bg-red-600"
          >
            Delete Account
          </button>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-md w-full animate-fadeIn">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Account?</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              This will permanently delete your account and all your recipes. This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                className="btn bg-red-500 text-white hover:bg-red-600"
              >
                Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-lg w-full animate-fadeIn">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Send Feedback</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Feedback Type
                </label>
                <select
                  value={feedbackType}
                  onChange={(e) => setFeedbackType(e.target.value)}
                  className="input"
                >
                  <option value="Feature Request">Feature Request</option>
                  <option value="Bug Report">Bug Report</option>
                  <option value="Question">Question</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your Message
                </label>
                <textarea
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                  placeholder="Describe your idea, issue, or question..."
                  rows={4}
                  className="input"
                />
              </div>

              {feedbackType === 'Bug Report' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Error Details (optional)
                  </label>
                  <textarea
                    value={feedbackError}
                    onChange={(e) => setFeedbackError(e.target.value)}
                    placeholder="If you saw an error message, paste it here..."
                    rows={2}
                    className="input"
                  />
                </div>
              )}

              <p className="text-sm text-gray-500 dark:text-gray-400">
                Your email address will be included so we can follow up if needed.
              </p>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => {
                  setShowFeedbackModal(false);
                  setFeedbackMessage('');
                  setFeedbackError('');
                }}
                className="btn btn-secondary"
                disabled={isSendingFeedback}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!feedbackMessage.trim()) return;
                  setIsSendingFeedback(true);
                  try {
                    await feedbackApi.send(
                      feedbackType,
                      feedbackMessage.trim(),
                      feedbackType === 'Bug Report' ? feedbackError.trim() : null
                    );
                    setShowFeedbackModal(false);
                    setFeedbackMessage('');
                    setFeedbackError('');
                    setSuccess('Thank you! Your feedback has been sent.');
                  } catch (err) {
                    setError(err.message || 'Failed to send feedback');
                  } finally {
                    setIsSendingFeedback(false);
                  }
                }}
                disabled={!feedbackMessage.trim() || isSendingFeedback}
                className="btn btn-primary"
              >
                {isSendingFeedback ? 'Sending...' : 'Send Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;
